import { useState } from 'react';
import { Navbar } from '../home/components/Navbar';
import { Footer } from '../home/components/Footer';

export default function ContactPage() {
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    message: ''
  });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formElement = e.target as HTMLFormElement;
      const formDataToSend = new FormData(formElement);
      
      const response = await fetch('FORM_SUBMIT_URL_PLACEHOLDER', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: new URLSearchParams(formDataToSend as any).toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({ name: '', email: '', message: '' });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="min-h-screen bg-gray-50">
      <Navbar />
      
      {/* Hero */}
      <section className="relative pt-32 pb-20 px-6 bg-gradient-to-br from-[#6B0000] to-[#4A0000]">
        <div className="max-w-7xl mx-auto text-center">
          <span className="inline-block px-4 py-1.5 bg-white/10 text-white text-xs font-bold uppercase tracking-widest rounded-full mb-6">
            Get In Touch
          </span>
          <h1 className="text-5xl md:text-6xl font-black text-white mb-6">
            Contact Us
          </h1>
          <p className="text-xl text-white/90 max-w-2xl mx-auto">
            Have questions about custom designs or orders? We're here to help!
          </p>
        </div>
      </section>

      {/* Contact Content */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12">
            {/* Contact Form */}
            <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
              <h2 className="text-3xl font-black text-gray-900 mb-6">
                Send Us a Message
              </h2>
              
              <form id="contact-form" data-readdy-form onSubmit={handleSubmit} className="space-y-6">
                <div>
                  <label htmlFor="name" className="block text-sm font-bold text-gray-900 mb-2">
                    Your Name
                  </label>
                  <input
                    type="text"
                    id="name"
                    name="name"
                    value={formData.name}
                    onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                    required
                    className="w-full px-4 py-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6B0000] focus:border-transparent"
                    placeholder="John Doe"
                  />
                </div>

                <div>
                  <label htmlFor="email" className="block text-sm font-bold text-gray-900 mb-2">
                    Email Address
                  </label>
                  <input
                    type="email"
                    id="email"
                    name="email"
                    value={formData.email}
                    onChange={(e) => setFormData({ ...formData, email: e.target.value })}
                    required
                    className="w-full px-4 py-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6B0000] focus:border-transparent"
                    placeholder="john@example.com"
                  />
                </div>

                <div>
                  <label htmlFor="message" className="block text-sm font-bold text-gray-900 mb-2">
                    Message
                  </label>
                  <textarea
                    id="message"
                    name="message"
                    value={formData.message}
                    onChange={(e) => setFormData({ ...formData, message: e.target.value })}
                    required
                    maxLength={500}
                    rows={6}
                    className="w-full px-4 py-3 text-sm border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-[#6B0000] focus:border-transparent resize-none"
                    placeholder="Tell us about your custom design needs..."
                  />
                  <p className="text-xs text-gray-500 mt-1">
                    {formData.message.length}/500 characters
                  </p>
                </div>

                {submitStatus === 'success' && (
                  <div className="p-4 bg-green-50 border border-green-200 rounded-lg">
                    <p className="text-sm font-semibold text-green-800">
                      Message sent successfully! We'll get back to you soon.
                    </p>
                  </div>
                )}

                {submitStatus === 'error' && (
                  <div className="p-4 bg-red-50 border border-red-200 rounded-lg">
                    <p className="text-sm font-semibold text-red-800">
                      Something went wrong. Please try again.
                    </p>
                  </div>
                )}

                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full px-8 py-4 bg-[#6B0000] text-white text-sm font-bold rounded-lg hover:bg-[#4A0000] transition-colors disabled:opacity-50 disabled:cursor-not-allowed whitespace-nowrap"
                >
                  {isSubmitting ? 'Sending...' : 'Send Message'}
                </button>
              </form>
            </div>

            {/* Contact Info */}
            <div className="space-y-8">
              {/* Location */}
              <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
                <div className="w-12 h-12 flex items-center justify-center bg-red-50 rounded-lg mb-4">
                  <i className="ri-map-pin-line text-2xl text-[#6B0000]"></i>
                </div>
                <h3 className="text-xl font-black text-gray-900 mb-2">
                  Visit Our Shop
                </h3>
                <p className="text-sm text-gray-600 mb-4">
                  1085 Stark Road Suite 106<br />
                  Starkville, MS 39759
                </p>
                <a
                  href="https://maps.google.com/?q=1085+Stark+Road+Suite+106+Starkville+MS+39759"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="inline-flex items-center gap-2 text-sm font-bold text-[#6B0000] hover:text-[#4A0000] transition-colors cursor-pointer"
                >
                  Get Directions
                  <i className="ri-arrow-right-line"></i>
                </a>
              </div>

              {/* Phone */}
              <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
                <div className="w-12 h-12 flex items-center justify-center bg-red-50 rounded-lg mb-4">
                  <i className="ri-phone-line text-2xl text-[#6B0000]"></i>
                </div>
                <h3 className="text-xl font-black text-gray-900 mb-2">
                  Call Us
                </h3>
                <a
                  href="tel:6622424286"
                  className="text-sm text-gray-600 hover:text-[#6B0000] transition-colors cursor-pointer"
                >
                  (662) 242-4286
                </a>
                <p className="text-xs text-gray-500 mt-2">Ask for Lisa</p>
              </div>

              {/* Email */}
              <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
                <div className="w-12 h-12 flex items-center justify-center bg-red-50 rounded-lg mb-4">
                  <i className="ri-mail-line text-2xl text-[#6B0000]"></i>
                </div>
                <h3 className="text-xl font-black text-gray-900 mb-2">
                  Email Us
                </h3>
                <a
                  href="mailto:info@ontargetprintsolutions.com"
                  className="text-sm text-gray-600 hover:text-[#6B0000] transition-colors cursor-pointer"
                >
                  info@ontargetprintsolutions.com
                </a>
                <p className="text-xs text-gray-500 mt-2">We reply within 24 hours</p>
              </div>

              {/* Hours */}
              <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
                <div className="w-12 h-12 flex items-center justify-center bg-red-50 rounded-lg mb-4">
                  <i className="ri-time-line text-2xl text-[#6B0000]"></i>
                </div>
                <h3 className="text-xl font-black text-gray-900 mb-4">
                  Store Hours
                </h3>
                <div className="space-y-2 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-600">Monday - Friday</span>
                    <span className="font-bold text-gray-900">9am - 6pm</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Saturday</span>
                    <span className="font-bold text-gray-900">10am - 4pm</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-600">Sunday</span>
                    <span className="font-bold text-gray-900">Closed</span>
                  </div>
                </div>
              </div>

              {/* Social */}
              <div className="bg-white rounded-2xl p-8 shadow-sm border border-gray-100">
                <h3 className="text-xl font-black text-gray-900 mb-4">
                  Follow Us
                </h3>
                <div className="flex gap-3">
                  <a
                    href="https://facebook.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded-lg hover:bg-[#6B0000] hover:text-white transition-colors cursor-pointer"
                  >
                    <i className="ri-facebook-fill text-lg"></i>
                  </a>
                  <a
                    href="https://instagram.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded-lg hover:bg-[#6B0000] hover:text-white transition-colors cursor-pointer"
                  >
                    <i className="ri-instagram-line text-lg"></i>
                  </a>
                  <a
                    href="https://twitter.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded-lg hover:bg-[#6B0000] hover:text-white transition-colors cursor-pointer"
                  >
                    <i className="ri-twitter-x-line text-lg"></i>
                  </a>
                  <a
                    href="https://tiktok.com"
                    target="_blank"
                    rel="noopener noreferrer"
                    className="w-10 h-10 flex items-center justify-center bg-gray-100 rounded-lg hover:bg-[#6B0000] hover:text-white transition-colors cursor-pointer"
                  >
                    <i className="ri-tiktok-line text-lg"></i>
                  </a>
                </div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}